import { FC, useEffect, useMemo, useRef, useState } from 'react';
import { Button, Checkbox, FormControl, InputAdornment, InputLabel, OutlinedInput, styled, Typography } from '@mui/material';
import { PolarisIcon } from '@/exports/columnCommonComponents';
import { useAppSelector } from '@/redux/hooks';
import { selectAudioLanguages, selectSubtitlesLanguages } from '@/redux/selectors/constantsSelectors';
import { ScheduleAvailType } from '@/redux/slices/slice_types';
import AvailListContainer, { StyledChildren, StyledScroll } from './AvailListContainer';
import NewScheduleAvailItem from './NewScheduleAvailItem';
import { FocusTheme } from '../../../configs/theme';
import { getAvailSourceByType } from '@/components/Focus/utils/newScheduleAvails';
import { useFormatAvailDisplayLabel } from '@/components/Focus/hooks/useFormatAvailDisplayLabel';
import { useAvailFiltering } from '@/components/Focus/hooks/useAvailFiltering';
import TerritoryGroupedAvailList from './TerritoryGroupAvailList';

type AddAvailListProps = {
  onItemAdd?: (right: string) => void;
  onItemsAdd?: (rights: string[]) => void;
  rightsOptions?: string[];
  //TODO: these aren't rights. selectedRights should be selectedOptiona
  selectedRightsList?: string[];
  type: ScheduleAvailType;
  scrollToElementName?: string;
  hideSelectAll?: boolean;
  onlyRights?: boolean;
};

const AddAvailList: FC<AddAvailListProps> = ({
  onItemAdd,
  onItemsAdd,
  rightsOptions = [],
  selectedRightsList,
  type,
  scrollToElementName,
  hideSelectAll = false,
  onlyRights = false
}) => {
  const scrollContainerRef = useRef<HTMLDivElement | null>(null);
  const audioOptions = useAppSelector(selectAudioLanguages)
    ?.filter(
      (a) => a.code !== 'any' && a.code !== 'engcc' && a.code !== 'notap' && (onlyRights ? rightsOptions.includes(a.code.replace('_', '-')) : true)
    )
    .sort((a, b) => a.label.localeCompare(b.label));
  const timedTextOptions = useAppSelector(selectSubtitlesLanguages)
    ?.filter(
      (s) => s.code !== 'any' && s.code !== 'engcc' && s.code !== 'notap' && (onlyRights ? rightsOptions.includes(s.code.replace('_', '-')) : true)
    )
    .sort((a, b) => a.label.localeCompare(b.label));

  const [query, setQuery] = useState('');
  const [showOnlyWithRights, setShowOnlyWithRights] = useState(false);

  const isTerritoriesWithGroups = type === 'territories';
  const formatDisplayLabel = useFormatAvailDisplayLabel();
  const formatDisplayLabelForType = useMemo(() => (code: string) => formatDisplayLabel(code, type), [formatDisplayLabel, type]);

  const { groupRights, rightsList } = useMemo(() => {
    const value = getAvailSourceByType(type, audioOptions, timedTextOptions);
    if (type === 'territories' && onlyRights) {
      // Filter groupRights to remove values where code doesn't exist in rightsOptions
      const filteredGroupRights = value.groupRights
        ?.map((group) => ({
          ...group,
          territories: group.territories.filter((code) => rightsOptions.includes(code))
        }))
        // Remove empty groups
        .filter((group) => group.territories.length > 0);

      return {
        ...value,
        groupRights: filteredGroupRights
      };
    }
    return value;
  }, [audioOptions, timedTextOptions, type, onlyRights, rightsOptions]);

  const { filteredGroupRights, filteredRights } = useAvailFiltering({
    formatDisplayLabel: formatDisplayLabelForType,
    groupRights,
    isTerritoriesWithGroups,
    query,
    rightsList,
    rightsOptions,
    selectedRightsList,
    showOnlyWithRights
  });

  const visibleRights = useMemo(() => {
    return isTerritoriesWithGroups ? filteredGroupRights.flatMap((group) => group.territories) : filteredRights;
  }, [filteredGroupRights, filteredRights, isTerritoriesWithGroups]);

  const selectableVisibleRights = useMemo(() => {
    if (rightsOptions.length === 0) return visibleRights;
    return visibleRights.filter((code) => rightsOptions.includes(code));
  }, [rightsOptions, visibleRights]);

  const rightsToAdd = useMemo(() => {
    return selectableVisibleRights.filter((code) => !selectedRightsList?.includes(code));
  }, [selectableVisibleRights, selectedRightsList]);

  const isAllSelected = rightsToAdd.length === 0 && selectableVisibleRights.length > 0;

  const handleAddMultipleRights = (rights: string[]) => {
    if (!rights.length) return;

    if (onItemsAdd) {
      onItemsAdd(rights);
      return;
    }

    rights.forEach((right) => onItemAdd?.(right));
  };

  const handleSelectAll = () => {
    handleAddMultipleRights(rightsToAdd);
  };

  useEffect(() => {
    if (scrollToElementName) {
      scrollContainerRef.current?.querySelector(`[data-scroll-to-element-name="${scrollToElementName}"]`)?.scrollIntoView();
    }
  }, [scrollToElementName]);

  return (
    <AvailListContainer>
      <div>
        <ActionsRow>
          <FormControl variant="outlined" sx={(theme) => ({ mb: theme.spacing(2), maxWidth: '50%', flexGrow: 1 })}>
            <InputLabel htmlFor="find-avails-input">Find...</InputLabel>
            <SearchInput
              id="find-avails-input"
              label="Find..."
              placeholder="Find..."
              onChange={(event) => setQuery(event.target.value)}
              value={query}
              startAdornment={
                <InputAdornment position="start">
                  <PolarisIcon icon="search" height={14} width={14} />
                </InputAdornment>
              }
            />
          </FormControl>
          <StyledButtonContainer>
            {(rightsOptions.length ?? 0) > 0 && !onlyRights && (
              <RightsFilter>
                <Checkbox checked={showOnlyWithRights} onChange={(event) => setShowOnlyWithRights(event.target.checked)} />
                <span>Active Rights</span>
              </RightsFilter>
            )}
            {!hideSelectAll && (
              <Button
                disabled={(!onItemAdd && !onItemsAdd) || !selectableVisibleRights.length || isAllSelected}
                onClick={handleSelectAll}
                variant="text"
              >
                Select all
              </Button>
            )}
          </StyledButtonContainer>
        </ActionsRow>
      </div>
      <StyledScroll ref={scrollContainerRef} data-type={type}>
        {isTerritoriesWithGroups ? (
          <>
            <StyledChildren>
              <TerritoryGroupedAvailList
                groups={filteredGroupRights}
                formatDisplayLabel={formatDisplayLabelForType}
                hasRights={(code) => rightsOptions.includes(code)}
                isSelected={(code) => !!selectedRightsList?.includes(code)}
                onItemClick={(code) => onItemAdd?.(code)}
                onItemsClick={handleAddMultipleRights}
                variant="default"
                scrollToElementName={scrollToElementName}
              />
            </StyledChildren>
            {filteredGroupRights.length === 0 && (
              <StyledEmptyState color="text.secondary" variant="body2">
                All territories selected
              </StyledEmptyState>
            )}
          </>
        ) : (
          <StyledChildren>
            {filteredRights.length === 0 ? (
              <StyledEmptyState color="text.secondary" variant="body2">
                All rights selected
              </StyledEmptyState>
            ) : (
              filteredRights.map((right) => (
                <NewScheduleAvailItem
                  key={right}
                  onClick={() => onItemAdd?.(right)}
                  primaryLabel={formatDisplayLabelForType(right)}
                  type="success"
                  variant="default"
                  isSelectedItem={!!selectedRightsList?.includes(right)}
                  hasRights={rightsOptions.includes(right)}
                />
              ))
            )}
          </StyledChildren>
        )}
      </StyledScroll>
    </AvailListContainer>
  );
};

export default AddAvailList;

const StyledEmptyState = styled(Typography)`
  text-align: center;
  padding: ${({ theme }) => theme.spacing(2)} 0;
`;

const ActionsRow = styled('div')`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  gap: ${({ theme }) => theme.spacing(2)};
`;

const RightsFilter = styled('div')`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(1)};
`;

const StyledButtonContainer = styled('div')`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  margin-right: ${({ theme }) => theme.spacing(3)};
  gap: ${({ theme }) => theme.spacing(2)};
  height: 53px;
  margin-bottom: ${({ theme }) => theme.spacing(2)};
`;

const SearchInput = styled(OutlinedInput)(({ theme }) => ({
  width: '100%',
  borderRadius: 6,
  backgroundColor: (theme as FocusTheme).palette.neutral[90],
  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: (theme as FocusTheme).palette.neutral[60],
    borderWidth: 1
  },
  '& .MuiInputAdornment-root': {
    color: (theme as FocusTheme).palette.neutral[30]
  }
}));
